import { Lang } from '@yandex-int/i18n/lib/react';
import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import { ThemeProvider, ToasterComponent, ToasterProvider } from '@gravity-ui/uikit';
import { InstallableAppPageContainer } from 'client/blocks-redesign/AppPage/InstallableAppPageContainer';
import { SkillAppPageContainer } from 'client/blocks-redesign/AppPage/SkillAppPageContainer';
import { AllAvailableAppsRouter } from 'client/blocks-redesign/AppsIndex/AllAvailableAppsRouter';
import AppsIndex from 'client/blocks-redesign/AppsIndex/AppsIndex';
import { reachGoal } from 'client/lib/metrika';
import { mapChannelToDefaultName } from 'client/utils/settings/alice-skill';
import { ALICE_APP_ROUTE } from 'common/routes';
import { keepIfInEnum } from 'common/utils/keepIfInEnum';
import block from 'propmods';

import { createApp, createSkill, getAppsMeta, getSnapshot } from '../../api';
import * as AppsMetaInfoContext from '../../context/appsMetaInfoContext';
import * as SnapshotContext from '../../context/snapshotContext';
import { useInterval } from '../../hooks/useInterval';
import { useMessageSubscription } from '../../hooks/useMessageSubscription';
import { useSessionFlags } from '../../hooks/useSessionFlags';
import { routes } from '../../lib/routes';
import { Channel } from '../../model/skill';
import { InstallableAppsMeta, Snapshot } from '../../types';
import {
    getIsYangoRequest,
    hrefIsExternal,
    isIframe,
    isInstallableAppChannel,
    showAppStoreUi,
    showAutoAppUi,
    showSkillsRedesign,
    showSmartScreenUi,
} from '../../utils';
import { tycoon } from '../../utils/messages';
import Announcement from '../Announcement/Announcement';
import AsyncProgress from '../AsyncProgress/AsyncProgress';
import { DocumentationPage } from '../DocumentationPage/DocumentationPage';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import Header from '../Header/Header';
import { Loading } from '../Loading/Loading';
import NewsPreview from '../NewsPreview/NewsPreview';
import SkillCreationPopup from '../SkillCreationPopup/SkillCreationPopup';
import SkillPageContainer from '../SkillPage/SkillPageContainer';
import SkillsIndex from '../SkillsIndex/SkillsIndex';

import SettingsPage from './AppSettingsPage';
import AuthReturnPage from './AuthReturnPage';
import EmailConfirmationPage from './EmailConfirmationPage';
import ModerationTestPage from './ModerationTestPage';
import UnsubscribePage from './UnsubscribePage';

import '@gravity-ui/uikit/styles/styles.css';

import './App.scss';

const { links, snapshotRefreshIntervalMs, urlRoot } = __appConfig.app;

interface CreatePopup {
    visible?: boolean;
    progress?: boolean;
}

const b = block('App');

const DEFAULT_THEME = 'light';

const isRedesignRoute = (location: ReturnType<typeof useLocation>, displaySkillsRedesign: boolean) => {
    if (location.pathname.startsWith(`${urlRoot}/${ALICE_APP_ROUTE[Channel.AutoApp]}`)) return true;
    if (location.pathname.startsWith(`${urlRoot}/${ALICE_APP_ROUTE[Channel.TvApp]}`)) return true;
    if (location.pathname.startsWith(`${urlRoot}/${ALICE_APP_ROUTE[Channel.SmartScreenApp]}`)) return true;
    if (location.pathname.startsWith(`${urlRoot}/all-available-apps`)) return true;

    if (displaySkillsRedesign) {
        if (location.pathname === urlRoot) return true;
        if (location.pathname === `${urlRoot}/${ALICE_APP_ROUTE[Channel.AliceSkill]}`) return true;
        if (location.pathname === `${urlRoot}/${ALICE_APP_ROUTE[Channel.SmartHome]}`) return true;
        if (location.pathname.includes('skills/')) return true;
    }

    return false;
};

const App: React.FC = () => {
    // Disable dark theme PASKILLS-9861
    // const [theme, setTheme] = useState<'light' | 'dark'>('light');

    // useEffect(() => {
    //     const updateColorScheme = () => {
    //         if (window.matchMedia) {
    //             if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //                 setTheme('dark');
    //             } else {
    //                 setTheme('light');
    //             }
    //         }
    //     };
    //     updateColorScheme();
    //     const colorSchemeQuery = !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    //     if (colorSchemeQuery) {
    //         colorSchemeQuery.addEventListener('change', updateColorScheme);
    //     }
    //     return () => {
    //         if (colorSchemeQuery) {
    //             colorSchemeQuery.removeEventListener('change', updateColorScheme);
    //         }
    //     };
    // }, []);

    const location = useLocation();

    if (isRedesignRoute(location, showSkillsRedesign())) {
        return (
            <ErrorBoundary withHomepageLink>
                <ThemeProvider theme={DEFAULT_THEME}>
                    <ToasterProvider>
                        <Switch>
                            <Route component={PrivateApp} />
                        </Switch>
                        <ToasterComponent />
                    </ToasterProvider>
                </ThemeProvider>
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary withHomepageLink>
            <ThemeProvider theme={'light'}>
                <div {...b({ 'in-iframe': isIframe() })}>
                    <Header />
                    {!getIsYangoRequest() && <NewsPreview />}
                    <Announcement />
                    <Switch>
                        <Route path={routes.app.email} exact component={EmailConfirmationPage} />
                        <Route path={routes.app.unsubscribe} exact component={UnsubscribePage} />
                        <Route path={routes.app.accountLinkingComplete} exact component={AuthReturnPage} />
                        <Route component={PrivateApp} />
                    </Switch>
                </div>
            </ThemeProvider>
        </ErrorBoundary>
    );
};

const PrivateApp: React.FC = () => {
    const history = useHistory();
    const user = __appConfig.user;

    useSessionFlags([
        '__display-smart-home-testing-page',
        '__display-cloud-functions',
        '__display-smart-home-cloud-functions',
        '__display-monitoring-page',
        '__display-smart-home-monitoring-page',
        '__display-donation-page',
        '__display-device-linking',
        '__display-app-metrica-settings',
        '__display-rsy-field',
        '__display-trusted-checkbox',
        '__display-anonymous-option',
        '__display-skill-access',
        '__display-sharing-page',
        '__display-testing-date-selector',
    ]);

    useMessageSubscription(async (data) => {
        switch (data.type) {
            case 'navigation':
                if (!hrefIsExternal.test(data.payload.path)) {
                    history.push(data.payload.path);
                }
                return;

            case 'sprav_dialogs_inited':
                tycoon.sendIframeInited();

                return;
            default:
                return;
        }
    });

    useEffect(() => {
        tycoon.sendIframeInited();
    }, []);

    if (!user) {
        const passportUrl = __appConfig.passport + '?retpath=' + encodeURIComponent(location.href);

        window.location.href = passportUrl;

        return (
            <div className="AppLoaderContainer">
                <Loading centered />
            </div>
        );
    }

    const skillMatchRouterLoose = [routes.app.settings(), routes.app.skill(), routes.app.documentation('')];

    const skillMatchRouterExact = [
        routes.app.moderation.test(),
        routes.app.skills,
        routes.app.installableApps(Channel.SmartHome),
        routes.app.newSkill(),
    ];

    const appMatchRouterLoose = [
        routes.app.allApps,
        routes.app.installableApp(Channel.TvApp),
        routes.app.installableApp(Channel.AutoApp),
        routes.app.installableApp(Channel.SmartScreenApp),
    ];

    const appMatchRouterExact = [
        routes.app.installableApps(Channel.TvApp),
        routes.app.installableApps(Channel.AutoApp),
        routes.app.installableApps(Channel.SmartScreenApp),
    ];

    return (
        <Switch>
            <Route path={skillMatchRouterLoose}>
                <SkillAppRoot />
            </Route>
            <Route path={skillMatchRouterExact} exact>
                <SkillAppRoot />
            </Route>
            <Route path={appMatchRouterLoose}>
                <InstallableAppRoot />
            </Route>
            <Route path={appMatchRouterExact} exact>
                <InstallableAppRoot />
            </Route>
            <Redirect to={routes.app.root} />
        </Switch>
    );
};

const SkillAppRoot = () => {
    const history = useHistory();
    const [snapshot, setSnapshot] = useState<Snapshot | undefined>();
    const [createPopup, setCreatePopup] = useState<CreatePopup>({});

    const fetchSkills = useCallback(async () => {
        try {
            setSnapshot(await getSnapshot());
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        const setup = async () => {
            try {
                setSnapshot(await getSnapshot());
            } catch (err) {
                console.error(err);
            }
        };
        setup();
    }, [fetchSkills]);

    useInterval(fetchSkills, snapshotRefreshIntervalMs);

    const createNewSkill = useCallback(
        async (channelValue: string | undefined) => {
            const channel = keepIfInEnum(channelValue, Channel);
            if (!channel || isInstallableAppChannel(channel)) {
                console.error('@App wrong channel type ', channelValue);
                return;
            }
            const skill =
                channel === Channel.Thereminvox
                    ? await createSkill(channel)
                    : await createApp(mapChannelToDefaultName[channel], channel);
            await fetchSkills();

            history.push(routes.app.skillPage(true, 'settings', skill.id));
            reachGoal('createSkill');
        },
        [fetchSkills, history],
    );

    const handleSkillCreationPopupSelect = useCallback(
        async (channel: Channel) => {
            setCreatePopup((prev) => ({ ...prev, progress: true }));

            await createNewSkill(channel);

            setCreatePopup((prev) => ({ ...prev, visible: false }));
        },
        [setCreatePopup, createNewSkill],
    );

    if (!snapshot) {
        return (
            <div className="AppLoaderContainer">
                <Loading centered />
            </div>
        );
    }

    return (
        <SnapshotContext.Provider value={{ snapshot, refreshSnapshot: fetchSkills, createSkill: createNewSkill }}>
            <Switch>
                <Route path={routes.app.moderation.test()} exact component={ModerationTestPage} />
                <Route path={routes.app.settings()} component={SettingsPage} />
                <Route path={routes.app.skills} exact>
                    {() => {
                        if (isIframe()) return null;
                        if (showSkillsRedesign()) {
                            return <AppsIndex channel={Channel.AliceSkill} />;
                        }
                        return (
                            <SkillsIndex
                                onCreateSkillClick={() =>
                                    __appConfig.isYangoRequest
                                        ? handleSkillCreationPopupSelect(Channel.SmartHome)
                                        : setCreatePopup({ visible: true })
                                }
                            />
                        );
                    }}
                </Route>
                <Route path={routes.app.installableApps(Channel.SmartHome)} exact>
                    {showSkillsRedesign() ? (
                        <AppsIndex channel={Channel.SmartHome} />
                    ) : (
                        <Redirect to={routes.app.root} />
                    )}
                </Route>
                <Route path={routes.app.skill()}>
                    {(props) =>
                        showSkillsRedesign() ? (
                            <SkillAppPageContainer skillId={props.match?.params.skillId as string} />
                        ) : (
                            <SkillPageContainer skillId={props.match?.params.skillId as string} />
                        )
                    }
                </Route>
                <Route path={routes.app.newSkill()} exact>
                    {(props) => <AsyncProgress action={() => createNewSkill(props.match?.params.channel)} />}
                </Route>
                <Route path={routes.app.documentation('')} component={DocumentationPage} />
            </Switch>

            <SkillCreationPopup
                visible={createPopup.visible}
                progress={createPopup.progress}
                onSelect={handleSkillCreationPopupSelect}
                onClose={() => setCreatePopup({ visible: false })}
                userAgreementLink={links.userAgreement}
            />
        </SnapshotContext.Provider>
    );
};

const InstallableAppRoot = () => {
    const history = useHistory();
    const [appsMetaInfo, setAppsMetaInfo] = useState<InstallableAppsMeta | undefined>();

    const fetchApps = useCallback(async () => {
        if (!showAppStoreUi()) {
            return;
        }

        try {
            const res = await getAppsMeta();
            setAppsMetaInfo(res.installable_apps);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchApps();
    }, [fetchApps]);

    useInterval(fetchApps, snapshotRefreshIntervalMs);

    const handleAppCreation = useCallback(
        async (channel: Channel) => {
            if (isInstallableAppChannel(channel)) {
                const { skillId } = await createApp(mapChannelToDefaultName[channel], channel);
                await fetchApps();
                history.push(routes.app.appPage(channel, true, 'settings', skillId));
            } else {
                const skill =
                    channel === Channel.Thereminvox
                        ? await createSkill(channel)
                        : await createApp(mapChannelToDefaultName[channel], channel);
                await fetchApps();
                history.push(routes.app.skillPage(true, 'settings', skill.id));
            }
        },
        [fetchApps, history],
    );

    return (
        <AppsMetaInfoContext.Provider
            value={{
                appsMetaInfo: appsMetaInfo ?? {
                    maxAppsPerUser: 0,
                    targetPlatformsByChannel: {},
                    categories: [],
                    ageRestrictions: [],
                },
                handleAppCreation,
            }}
        >
            <Switch>
                <Route path={routes.app.installableApps(Channel.TvApp)} exact>
                    <AppsIndex channel={Channel.TvApp} />
                </Route>
                <Route path={routes.app.installableApps(Channel.AutoApp)} exact>
                    {showAutoAppUi() ? <AppsIndex channel={Channel.AutoApp} /> : <Redirect to={routes.app.root} />}
                </Route>
                <Route path={routes.app.installableApps(Channel.SmartScreenApp)} exact>
                    {showSmartScreenUi() ? (
                        <AppsIndex channel={Channel.SmartScreenApp} />
                    ) : (
                        <Redirect to={routes.app.root} />
                    )}
                </Route>
                <Route path={routes.app.allApps}>
                    <AllAvailableAppsRouter />
                </Route>
                <Route path={routes.app.installableApp(Channel.TvApp)}>
                    {(props) => (
                        <InstallableAppPageContainer
                            channel={Channel.TvApp}
                            skillId={props.match?.params!.skillId as string}
                        />
                    )}
                </Route>
                <Route path={routes.app.installableApp(Channel.AutoApp)}>
                    {showAutoAppUi() ? (
                        (props) => (
                            <InstallableAppPageContainer
                                channel={Channel.AutoApp}
                                skillId={props.match?.params!.skillId as string}
                            />
                        )
                    ) : (
                        <Redirect to={routes.app.root} />
                    )}
                </Route>
                <Route path={routes.app.installableApp(Channel.SmartScreenApp)}>
                    {showSmartScreenUi() ? (
                        (props) => (
                            <InstallableAppPageContainer
                                channel={Channel.SmartScreenApp}
                                skillId={props.match?.params!.skillId as string}
                            />
                        )
                    ) : (
                        <Redirect to={routes.app.root} />
                    )}
                </Route>
            </Switch>
        </AppsMetaInfoContext.Provider>
    );
};

const RoutedApp: React.FC = () => (
    <BrowserRouter>
        <Lang.Provider value={{ lang: __appConfig.lang }}>
            <App />
        </Lang.Provider>
    </BrowserRouter>
);

export { RoutedApp as App };
